import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  HeartPulse,
  Brain,
  PersonStanding,
  Sparkles,
  Droplets,
  Apple,
  Users,
} from "lucide-react";
import { AMBASSADOR_IMG, HERO_PROGRAMS } from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";
import { scrollToHash } from "@/lib/scroll";

const ICONS = { ShieldCheck, HeartPulse, Brain, PersonStanding, Sparkles, Droplets, Apple, Users };

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { openBook } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#070604] pt-[72px] md:pt-20"
    >
      <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="/assets/site/hero-bokeh2.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90 brightness-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070604]/85 via-[#070604]/40 to-[#070604]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070604]/90 via-transparent to-[#070604]/30" />
        <div className="absolute top-[-20%] left-[45%] w-[60vw] h-[60vw] rounded-full bg-[#c9a14a]/[0.09] blur-[120px]" />
        <div className="absolute bottom-[-30%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-[#3a2c12]/25 blur-[100px]" />
      </motion.div>
      <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-[1480px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-[1.05fr_1fr_auto] gap-10 lg:gap-6 items-center min-h-[calc(100vh-80px)] py-14 lg:py-0">
          <div className="relative z-10 max-w-xl">
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[4.4rem] leading-[1.04]">
              <span className="block overflow-hidden">
                <motion.span variants={lineReveal} initial="hidden" animate="show" custom={0} className="block text-[#f7f2e8]">
                  Not a Spa.
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span variants={lineReveal} initial="hidden" animate="show" custom={1} className="block text-[#c9a14a]">
                  A Wellness Center.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-6 text-[#f7f2e8]/80 text-base md:text-lg leading-relaxed max-w-md"
              data-testid="hero-subtitle"
            >
              India's premium protocol-led wellness network for prevention, recovery,
              slimming, anti-ageing, mental wellbeing and lifestyle health.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <button onClick={() => openBook("assessment")} data-testid="hero-book-assessment-button" className="btn-gold">
                Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
              </button>
              <button
                onClick={() => scrollToHash("#programs")}
                data-testid="hero-explore-programs-button"
                className="btn-outline-gold !text-[#f7f2e8] hover:!text-[#050505]"
              >
                Explore Programs
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative flex items-end justify-center lg:h-[calc(100vh-80px)] order-3 lg:order-none"
          >
            <motion.div style={{ y: imgY }} className="relative flex items-end justify-center lg:h-full">
              <div
                aria-hidden="true"
                className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-[85%] aspect-square rounded-full bg-[#c9a14a]/[0.13] blur-[90px] pointer-events-none"
              />
              <img
                src={AMBASSADOR_IMG}
                alt="Mrunal Thakur — Brand Ambassador, Carlton Wellness Center"
                data-testid="hero-ambassador-image"
                className="relative w-full max-w-[420px] lg:max-w-none lg:h-[94%] lg:w-auto object-contain object-bottom select-none pointer-events-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              />
            </motion.div>
          </motion.div>

          <div
            className="relative z-10 grid grid-cols-2 lg:flex lg:flex-col gap-x-4 gap-y-5 lg:gap-6 lg:pr-2 pb-10 lg:pb-0"
            data-testid="hero-program-list"
          >
            {HERO_PROGRAMS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, delay: 0.9 + i * 0.09 }}
                >
                  <Link
                    to={`/programs/${p.slug}`}
                    data-testid={`hero-program-${i}`}
                    className="flex items-center gap-3 text-left group"
                  >
                    <span className="w-10 h-10 shrink-0 rounded-full border border-[#c9a14a]/60 flex items-center justify-center text-[#c9a14a] transition-all duration-300 group-hover:bg-[#c9a14a] group-hover:text-[#050505]">
                      <Icon size={17} strokeWidth={1.5} />
                    </span>
                    <span className="text-[12px] lg:text-[13px] text-[#f7f2e8]/90 group-hover:text-[#c9a14a] transition-colors duration-300">
                      {p.label}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
